import Drawer from '@material-ui/core/Drawer';
import {Button} from '@wandb/weave/components';
import React, {useState} from 'react';

import {useTeamPageQuery, WorkflowsAdminType} from '../../../generated/graphql';
import * as C from '../../../pages/HomePage/HomePageSidebar/CommonDrawer.styles';
import * as S from '../../../pages/HomePage/HomePageSidebar/CreateProjectDrawer.styles';
import {Analytics} from '../../../services/analytics';
import {AccessOption} from '../../../util/permissions';
import {ResizableDrawer} from '../../Launch/ResizableDrawer';
import {useAddProjectMembers} from '../hooks';
import {MemberInviteOptions, UserRoleItem} from './MemberInviteOptions';
import {ProjectRoleMember} from './utils';

export interface AddProjectMembersDrawerProps {
  isOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  currentProjectMembers: ProjectRoleMember[];
  projectTeamName: string;
  projectName: string;
  projectID: string;
  projectAccess: AccessOption;
  handleModalClose(): void;
}

// Use functionality from AddRegistryMembersModal, but CreateProjectDrawer styling
export const AddProjectMembersDrawer = ({
  isOpen,
  setIsModalOpen,
  handleModalClose,
  currentProjectMembers,
  projectTeamName,
  projectName,
  projectID,
  projectAccess,
}: AddProjectMembersDrawerProps) => {
  const [width, setWidth] = useState(500);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const currentProjectMembersIds = currentProjectMembers.map(m => m.id);
  const usersToAdd =
    projectAccess === AccessOption.Restricted
      ? selectedUserIds.filter(u => !currentProjectMembersIds?.includes(u))
      : [];

  const {addProjectMembers, isLoading: isProjectMembersLoading} =
    useAddProjectMembers(projectTeamName, projectName);

  const {data: teamPageQuery, loading: isTeamPageLoading} = useTeamPageQuery({
    skip: projectTeamName === '',
    variables: {
      entityName: projectTeamName,
      workflowsAdminType: WorkflowsAdminType.Model,
    },
  });

  const isButtonDisabled = usersToAdd.length === 0;

  const onClickConfirm = async () => {
    await addProjectMembers(projectID, usersToAdd);
    Analytics.track('Project interaction', {
      action: 'add members',
      projectTeamName,
      projectName,
    });
    handleModalClose();
  };
  const onOpenChange = (open: boolean) => {
    if (!isProjectMembersLoading || isTeamPageLoading) {
      setIsModalOpen(open);
    }
    handleModalClose();
  };

  const members =
    teamPageQuery?.entity?.members.map(teamMember => ({
      username: teamMember.username,
      name: teamMember.name,
      email: teamMember.email,
      id: teamMember.id,
      accountType: teamMember.accountType,
      photoUrl: teamMember.photoUrl,
      role: teamMember.memberRole?.name,
    })) ?? [];

  const projectMembers = currentProjectMembers.map(u => ({
    username: u.username,
    name: u.name ? u.name : '',
    email: u.email,
    id: u.id,
    accountType: u.accountType,
    photoUrl: u.photoUrl,
    role: u.role.name,
  }));

  const selectedUsers = members?.filter(m => m.id && usersToAdd.includes(m.id));

  const userItems = [...projectMembers, ...selectedUsers].map(u => {
    return (
      <UserRoleItem key={u.id} user={u} entityName={projectTeamName ?? ''} />
    );
  });

  return (
    <Drawer anchor="right" open={isOpen} onClose={onOpenChange}>
      <ResizableDrawer startWidth={width} minWidth={400} onSetWidth={setWidth}>
        <C.DrawerContents>
          <C.TopPanel>
            <C.HeaderRow>
              <C.Header>Add new user(s)</C.Header>
              <C.Close>
                <Button
                  onClick={handleModalClose}
                  icon="close"
                  size="large"
                  variant="ghost"
                />
              </C.Close>
            </C.HeaderRow>
            <C.UnderLine />

            <S.FormRow>
              <C.Label>Invite users</C.Label>
              <div className="mt-24 mt-4 flex items-center">
                <MemberInviteOptions
                  selectedUsers={selectedUserIds}
                  setSelectedUsers={setSelectedUserIds}
                  currentMembersIds={currentProjectMembersIds}
                  entityMembers={members}
                />
                {userItems}
              </div>
            </S.FormRow>
          </C.TopPanel>
          <Button
            className="w-full"
            variant="primary"
            size="large"
            data-test="create-project"
            disabled={isButtonDisabled}
            onClick={onClickConfirm}>
            Add new user(s)
          </Button>
        </C.DrawerContents>
      </ResizableDrawer>
    </Drawer>
  );
};
